import React, {useCallback, useMemo} from 'react';

import {WarningIcon} from 'src/components/error-flat-button';
import {
    EMAIL_SETTINGS,
    FORBIDDEN_FOR_MANDATORY_SSO_ERROR,
    FORBIDDEN_FOR_TRIAL_ERROR, INACTIVE_OR_DISABLED_LOGIN_ERROR,
    NO_AM_ERROR,
    ONLY_SSO_LOGIN_FLOW_ERROR,
} from 'src/constants';
import {EmailType as EmailTypes} from 'src/openapi/admin/models/EmailType';

import {Group} from '@pb/kit-component-group';
import {Info} from '@pb/kit-component-info';
import Label from '@pb/kit-component-label';
import {RadioButton, RadioButtons} from '@pb/kit-component-radio-button';


import type {AccountInfo} from 'src/openapi/admin/models/AccountInfo';
import type {AccountManagerInfo} from 'src/openapi/admin/models/AccountManagerInfo';
import type {EmailType} from 'src/openapi/admin/models/EmailType';
import type {SsoSettings} from 'src/use-cases/account/useGetAccountSSOSettings';

const teamMemberFreeEmail = [
    EmailTypes.WELCOME_EMAIL,
    EmailTypes.STARTUP_WELCOME_EMAIL,
    EmailTypes.NEW_CLIENT_WELCOME_EMAIL,
    EmailTypes.ORIGIN_WELCOME_EMAIL,
];

// Always available: user status/licence, account type and SSO settings never block it.
// The variant (password vs SSO) is picked on the backend.
const alwaysAvailableEmail = [
    EmailTypes.ORIGIN_WELCOME_EMAIL,
];

// Gated on account SSO only: user status, licence and account type never block these.
const ssoEnabledOnlyEmail = [
    EmailTypes.LLM_ONLY_WELCOME_EMAIL,
];

type Props = {
    emailType: EmailType | null,
    setEmailType: (emailType: EmailType | null) => void,
    teamMembers: AccountManagerInfo[],
    accountInfo: AccountInfo | null,
    ssoSettings: SsoSettings,
    disabled: boolean,
};

export const SelectEmailType: React.FC<Props> = (
    {
        emailType,
        setEmailType,
        teamMembers,
        accountInfo,
        ssoSettings,
        disabled,
    },
) => {

    const isAmNotConfigured = useMemo(() => {
        return !teamMembers.find(({managerType}) => managerType === 'AM');
    }, [teamMembers]);

    const getDisabledState = useCallback((checkedType: EmailType) => {
        if (alwaysAvailableEmail.includes(checkedType)) {
            return false;
        }

        if (ssoEnabledOnlyEmail.includes(checkedType)) {
            return !ssoSettings.ssoEnabled;
        }

        if (disabled) {
            return true;
        }

        if (!teamMemberFreeEmail.includes(checkedType) && isAmNotConfigured) {
            return true;
        }

        if (accountInfo?.entityTypeCode !== 'Trial') {
            if (checkedType === EmailTypes.AM_WELCOME_EMAIL_TRIAL) {
                return true;
            }

            if (!ssoSettings.ssoEnabled) {
                return EMAIL_SETTINGS[checkedType].ssoMandatory;
            }

            return ssoSettings.ssoMandatory
                ? !EMAIL_SETTINGS[checkedType].ssoMandatory
                : EMAIL_SETTINGS[checkedType].ssoEnabled;
        }

        return checkedType !== EmailTypes.AM_WELCOME_EMAIL_TRIAL;
    }, [accountInfo, ssoSettings, isAmNotConfigured, disabled]);

    const renderWarningIcon = useCallback((type: EmailType) => {
        if (alwaysAvailableEmail.includes(type)) {
            return null;
        }

        if (ssoEnabledOnlyEmail.includes(type)) {
            return ssoSettings.ssoEnabled ? null : <WarningIcon text={ONLY_SSO_LOGIN_FLOW_ERROR}/>;
        }

        if (disabled) {
            return <WarningIcon text={INACTIVE_OR_DISABLED_LOGIN_ERROR}/>;
        }

        if (isAmNotConfigured && !teamMemberFreeEmail.includes(type)) {
            return <WarningIcon text={NO_AM_ERROR}/>;
        }

        if (type === EmailTypes.AM_WELCOME_EMAIL_TRIAL && accountInfo?.entityTypeCode !== 'Trial') {
            return <WarningIcon text={FORBIDDEN_FOR_TRIAL_ERROR}/>;
        }

        if (![EmailTypes.AM_WELCOME_EMAIL_SSO, EmailTypes.AM_WELCOME_EMAIL_SSO_NO_GIFT].includes(type)
            && ssoSettings.ssoMandatory) {
            return <WarningIcon text={FORBIDDEN_FOR_MANDATORY_SSO_ERROR}/>;

        }
        if (EMAIL_SETTINGS[type].ssoMandatory && !ssoSettings.ssoEnabled) {
            return <WarningIcon text={ONLY_SSO_LOGIN_FLOW_ERROR}/>;

        }

        return null;
    }, [isAmNotConfigured, ssoSettings, accountInfo?.entityTypeCode, disabled]);

    return (
        <Group direction="v" gap="m">
            <Label>Send Welcome Email Type:</Label>
            <RadioButtons
                name="Selected Email Type"
                value={emailType}
                onChange={setEmailType}>
                {Object.values(EmailTypes)
                    .map(type => {
                        const {key, value, label, infoPopover} = EMAIL_SETTINGS[type];
                        return (
                            <Group key={key} direction="h" gap="s">
                                <RadioButton
                                    key={key}
                                    value={value}
                                    label={label}
                                    disabled={getDisabledState(type)}
                                />
                                <Info type={{popover: infoPopover}}/>
                                {renderWarningIcon(type)}
                            </Group>
                        );
                    })}
            </RadioButtons>
        </Group>
    );
};
